import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DarxApplicationDtOsAdsDtOsAdsGetAllDto } from 'service/api/allApis';
import Hashtag from 'components/common/hashtag';
import { ReactComponent as FloorIcon } from 'assets/icons/floor.svg';
import { ReactComponent as HomeIcon } from 'assets/icons/home.svg';
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg';
import { ReactComponent as VolumeIcon } from 'assets/icons/volume.svg';
import { ReactComponent as RoomIcon } from 'assets/icons/room.svg';
import { useLanguage } from 'hooks/use-language';
import { ADVERT_TYPE, MAINTENANCE_STATUS, PRICE_TYPE, PROPERTY_TYPE } from 'types';
import { RenderIf } from 'components/common/render-if';
import {
    formatDate,
    formatPrice,
    getShowFloorInfo,
    getShowMaintenance,
    getShowRoomCount,
} from 'lib/utils';
import { useSelector } from 'react-redux';
import { selectSettings } from 'store/features/settingsSlice';
import { ClockIcon, HammerIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Skeleton } from 'components/common/skeleton';

type Props = {
    advert: DarxApplicationDtOsAdsDtOsAdsGetAllDto;
};

const AdvertSlide = ({ advert }: Props) => {
    const { t } = useTranslation();
    const { selectedLanguageId, selectedLanguageCode } = useLanguage();
    const { isFullPrice, currency } = useSelector(selectSettings);

    const country = useMemo(
        () =>
            selectedLanguageId
                ? advert.country?.countryDictionaryGetAllDtos?.find(
                      (i) => i.languageId === selectedLanguageId,
                  )?.name
                : advert.country?.countryDictionaryGetAllDtos?.[0].name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );
    const city = useMemo(
        () =>
            selectedLanguageId
                ? advert.city?.cityDictionaryGetAllDtos?.find(
                      (i) => i.languageId === selectedLanguageId,
                  )?.name
                : advert.city?.cityDictionaryGetAllDtos?.[0].name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );

    const district = useMemo(
        () =>
            advert.district?.districtDictionaryGetAllDtos?.find(
                (i) => !selectedLanguageId || i.languageId === selectedLanguageId,
            )?.name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );
    const metro = useMemo(
        () =>
            advert.metro?.metroDictionaryGetAllDtos?.find(
                (i) => !selectedLanguageId || i.languageId === selectedLanguageId,
            )?.name,
        [selectedLanguageId, advert.country?.countryDictionaryGetAllDtos],
    );

    const showFloorInfo = getShowFloorInfo(advert.propertyType! as PROPERTY_TYPE);
    const showMaintenance = getShowMaintenance(advert.propertyType! as PROPERTY_TYPE);
    const showRoomCount = getShowRoomCount(
        advert.propertyType! as PROPERTY_TYPE,
        advert.roomCount ?? 0,
    );

    let title;
    if (district) {
        title = district;
    }
    if (metro) {
        title += ', ' + metro;
    }
    if (!title) {
        title = city;
    }

    let address = `${city}`;
    if (district) {
        address += `, ${district}`;
    }
    if (metro) {
        address += `, ${metro}`;
    }
    const price =
        isFullPrice || advert.advertType !== ADVERT_TYPE.SALE
            ? advert.price
            : Math.round(advert.price! / advert.propertyArea!) || 1;
    const priceType =
        advert.priceType === PRICE_TYPE.MONTHLY ? t('advert.monthly') : t('advert.daily');
    const maintenanceStatus =
        advert.maintenanceStatus === MAINTENANCE_STATUS.FULL
            ? t('advert.full-repair')
            : advert.maintenanceStatus === MAINTENANCE_STATUS.AVERAGE
            ? t('advert.average-repair')
            : t('advert.without-repair');

    return (
        <Link to={`/advert/${advert.id}`}>
            <div className="grid grid-cols-[525px,1fr] lg:grid-cols-[544px,1fr] md:gap-x-5 lg:gap-x-[62px]">
                <div className="md:w-[525px] lg:w-[544px] pl-5">
                    <Hashtag label={t(`general.${advert.advertType?.toLowerCase()}`)} />
                    <h2 className="text-txt text-[28px] font-bold my-4">{title}</h2>
                    <p className="text-txt-40 text-sm font-semibold mb-6 line-clamp-4">
                        {advert.description}
                    </p>
                    <div
                        className="min-w-[335px] w-fit min-h-[100px] flex  gap-x-[18px] pl-6 rounded-2xl pr-4 py-4"
                        style={{
                            boxShadow: '2px 6px 20px 0px rgba(0, 0, 0, 0.15)',
                        }}
                    >
                        <HomeIcon />
                        <div>
                            <p className="text-brend font-bold text-base tracking-[0.64px]">
                                {formatPrice(price, +currency.exchangeRate)} {currency.code}
                                <RenderIf condition={advert.advertType === ADVERT_TYPE.RENT}>
                                    <span className="lowercase text-brend/80 text-[8px] sm:text-[10px] md:text-[12px] font-bold translate-y-0.5">
                                        /{priceType}
                                    </span>
                                </RenderIf>
                            </p>
                            <div className="max-w-[240px] flex flex-wrap gap-3 mt-2.5 text-txt-40 text-xs font-semibold">
                                <p className="flex gap-x-1 items-center">
                                    <VolumeIcon />
                                    {advert.propertyArea}
                                    {advert.propertyType === PROPERTY_TYPE.LAND ? 'sot' : 'm²'}
                                </p>
                                <RenderIf condition={showRoomCount}>
                                    <p className="flex gap-x-1 items-center">
                                        <RoomIcon className="md:w-fit md:h-fit w-[10px] h-[10px]" />
                                        {t('advert.room-with-count', { count: advert.roomCount! })}
                                    </p>
                                </RenderIf>
                                <RenderIf condition={showFloorInfo}>
                                    <p className="flex gap-x-1 items-center">
                                        <FloorIcon /> {advert.propertyFloor}/
                                        {advert.buildingFloorCount}
                                    </p>
                                </RenderIf>
                                <RenderIf condition={showMaintenance}>
                                    <p className="flex gap-x-1 items-center">
                                        <HammerIcon className="w-4 h-4" />
                                        {maintenanceStatus}
                                    </p>
                                </RenderIf>
                                <p className="flex gap-x-1 items-center">
                                    <ClockIcon className="w-4 h-4" />
                                    {formatDate({
                                        inputDate: advert.date!,
                                        locale: selectedLanguageCode!,
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div
                        className="min-w-[335px] w-fit h-[100px] mt-4 flex items-center gap-x-[18px] pl-6 rounded-2xl"
                        style={{
                            boxShadow: '2px 6px 20px 0px rgba(0, 0, 0, 0.15)',
                        }}
                    >
                        <LocationIcon />
                        <div>
                            <p className="text-brend font-bold text-base tracking-[0.64px]">
                                {country}
                            </p>
                            <p className="flex gap-x-1.5 items-center mt-2.5 text-txt-40 text-xs font-semibold underline">
                                {address}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="pr-5 h-[420px] grid grid-cols-[9fr,8fr] gap-x-5 ">
                    <img
                        src={advert.images?.[0].largeImage ?? ''}
                        alt="Advert 1"
                        className="mt-auto lg:mt-0 md:h-[316px]  lg:h-[420px] object-cover rounded-2xl"
                        style={{
                            boxShadow: '1px 4px 16px 0px rgba(0, 0, 0, 0.15)',
                        }}
                    />
                    <div className="flex flex-col gap-y-5 lg:-translate-y-10">
                        <img
                            src={advert.images?.[1].largeImage ?? ''}
                            alt="Advert 2"
                            className="md:h-[190px]  lg:h-[250px] object-cover rounded-2xl"
                            style={{
                                boxShadow: '1px 4px 16px 0px rgba(0, 0, 0, 0.15)',
                            }}
                        />
                        <img
                            src={advert.images?.[2].largeImage ?? ''}
                            alt="Advert 3"
                            className=" h-[150px] object-cover rounded-2xl"
                            style={{
                                boxShadow: '1px 4px 16px 0px rgba(0, 0, 0, 0.15)',
                            }}
                        />
                    </div>
                </div>
            </div>
        </Link>
    );
};

AdvertSlide.Skeleton = () => (
    <div className="absolute top-10 left-0 w-full">
        <div className="grid grid-cols-[525px,auto] lg:grid-cols-[544px,1fr] md:gap-x-5 lg:gap-x-[62px] w-full">
            <div className="md:w-[525px] lg:w-[544px] pl-5">
                <Skeleton className="w-20 h-6" />
                <Skeleton className="w-72 h-8 mt-5" />
                <Skeleton className="w-96 h-16 mt-5" />
                <Skeleton className="w-[335px] h-28 rounded-2xl mt-5" />
                <Skeleton className="w-[335px] h-[100px] mt-[18px] rounded-2xl" />
            </div>
            <div className="pr-5 h-[420px] gap-x-5 z-50 grid grid-cols-[9fr,8fr]">
                <Skeleton className="mt-auto lg:mt-0 md:h-[316px]  lg:h-[420px] rounded-2xl" />
                <div className="flex flex-col gap-y-5 lg:-translate-y-10">
                    <Skeleton className="md:h-[190px]  lg:h-[250px]  rounded-2xl" />
                    <Skeleton className=" h-[150px] rounded-2xl" />
                </div>
            </div>
        </div>
    </div>
);

export default AdvertSlide;
